const SHEETAPIKEY = "AIzaSyAE-7Nmyo9EpqeXTAT0becj7wGI-sc7b_g";
// const SHEETID = "1u25CPlFZDkUxVXueS6isB8wIJWHbgBY_cnZMePy9gJg";
const SHEETID = "1NPI22p9bKXc-nwZMr4alH24snv2nk4Fn16A1ZaRyVno";

/**
 * @typedef {(
 * | 'Sunday'
 * | 'Monday'
 * | 'Tuesday'
 * | 'Wednesday'
 * | 'Thursday'
 * | 'Friday'
 * | 'Saturday'
 * )} DayOfWeek
 */
const DaysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturaday",
];

const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;

/**
 * @typedef {(
 * | 'Open'
 * | 'Closed'
 * | 'Limited Hours'
 * | 'Lobby: Appt Only'
 * | 'Drive-Thru Only'
 * | 'Lobby Only'
 * | 'Delayed Opening'
 * )} Status
 */

/**
 * @typedef {Object} Place
 * @property {string} name
 * @property {string} phone
 * @property {OpeningHoursSpecification[]} lobby opening hours for the lobby
 * @property {string} lobbyHours
 * @property {OpeningHoursSpecification[]} driveThru opening hours for the drive-thru
 * @property {string} driveThruHours
 * @property {Status} status
 * @property {string} message
 */

/**
 * @typedef {Object} OpeningHoursSpecification
 * @property {string} opens The opening hour of the place or service on the given day(s) of the week.
 * @property {string} closes The closing hour of the place or service on the given day(s) of the week.
 * @property {DayOfWeek} dayOfWeek The day of the week for which these opening hours are valid.
 */

/**
 * Fetches locations from spreadsheet.
 *
 * @param {Object} [options]
 * @param {string} [options.sheetId]
 * @param {string} [options.apiKey]
 * @return {Promise<Object<string, Place>>}
 */
globalThis.fetchLocations = async function(options = {}) {
  const {
    sheetId = SHEETID,
    apiKey = SHEETAPIKEY,
    timeZone = "America/Chicago",
  } = options;

  const baseUrl = new URL(
    `${sheetId}/values`,
    `https://sheets.googleapis.com/v4/spreadsheets/`
  );

  await gapi.client.init({
    apiKey,
  });

  const [locationsResponse, holidaysResponse] = await Promise.all([
    gapi.client.request({ path: `${baseUrl}/Locations!A2:Z` }),
    gapi.client.request({ path: `${baseUrl}/Banking Holidays!A2:B` }),
  ]);

  const now = new Date();

  // Get current date/time at the timeZone
  const nowAtTimeZone = now.toLocaleString("en-US", {
    timeZone,
  });
  const todayAtTimeZone = new Date(nowAtTimeZone);

  let nearestHoliday;
  holidaysResponse.result?.values?.forEach(([date, message]) => {
    const holiday = new Date(date);
    const daysToHoliday = (holiday - todayAtTimeZone) / MILLISECONDS_IN_DAY;
    if (daysToHoliday < 0) return;
    if (daysToHoliday > 7) return;

    nearestHoliday = {
      at: holiday,
      in: daysToHoliday,
      message,
    }
  });

  const places = {};
  if (locationsResponse.result?.values.length > 0) {
    locationsResponse.result.values.forEach((row) => {
      /**
       * @type {string[]}
       */
      const [
        name,
        phone,
        lobbyOpen,
        lobbyClose,
        lobbyM, lobbyT, lobbyW, lobbyTh, lobbyF,
        driveThruOpen,
        driveThruClose,
        dM, dT, dW, dTh, dF,
        status,
      ] = row;

      /**
       * @type {OpeningHoursSpecification[]}
       */
      const lobby = [
        false,
        lobbyM, lobbyT, lobbyW, lobbyTh, lobbyF,
        false,
      ].map((opensThatDay, dayOfWeek) => {
        if (opensThatDay !== "TRUE") return false;
        return {
          opens: lobbyOpen,
          closes: lobbyClose,
          dayOfWeek,
        }
      }).filter(Boolean);

      /**
       * @type {OpeningHoursSpecification[]}
       */
      const driveThru = [
        false,
        dM, dT, dW, dTh, dF,
        false,
      ].map((opensThatDay, dayOfWeek) => {
        if (opensThatDay !== "TRUE") return null;
        return {
          opens: driveThruOpen,
          closes: driveThruClose,
          dayOfWeek,
        }
      }).filter(Boolean);

      /**
       * @type {Place}
       */
      const place = {
        name, phone,
        lobby,
        lobbyHours: `Monday - Friday \n ${lobbyOpen} - ${lobbyClose}`,
        driveThru,
        driveThruHours: driveThruOpen ? `Monday - Friday \n ${driveThruOpen} - ${driveThruClose}` : "",
        status,
      }
      places[name] = place;

      // If status in spreadsheet is Closed, it's closed.
      if (status === "Closed") {
        return;
      }

      if (nearestHoliday) {
        place.message = nearestHoliday.message;

        if (nearestHoliday.in === 0) {
          // It's a holiday!
          place.status = "Closed";
          return;
        }
      }

      // Only compute actual status when it's Open in spreadsheet.
      if (isOpen(place.lobby, todayAtTimeZone, timeZone)) {
        // If other status was chosen, display that beneath Open,
        // but only when the computed state is "Open".
        if (status !== "Open") {
          // place.status = "Open, " + status;
        }
      } else {
        place.status = "Closed";
      }
    });
  }
  return places;
}

/**
 * Computes whether the params indicate open.
 * @param {OpeningHoursSpecification[]} openingHours
 * @param {Date} todayAtTimeZone the current date at the timeZone.
 * @param {string} timeZone
 * @returns {Boolean}
 */
function isOpen(openingHours, todayAtTimeZone, timeZone = "America/Chicago") {
  const now = new Date();

  if (!todayAtTimeZone) {
    // Get current date/time at the timeZone
    const nowAtTimeZone = now.toLocaleString("en-US", {
      timeZone,
    });
    todayAtTimeZone = new Date(nowAtTimeZone);
  }

  // Get weekday at timeZone (e.g., 1 for "Monday")
  const dayAtTimeZone = todayAtTimeZone.getDay();

  const openDay = openingHours.find(({ dayOfWeek }) => dayOfWeek === dayAtTimeZone);
  if (!openDay) {
    return false;
  }

  // Converts AM/PM open/close time to full date.
  const opensAtTimeZone = new Date(`${todayAtTimeZone.toDateString()} ${openDay.opens}`);
  const closesAtTimeZOne = new Date(`${todayAtTimeZone.toDateString()} ${openDay.closes}`);

  const opensAtLocal = new Date(
    opensAtTimeZone.toLocaleString("en-US", { timeZone })
  );
  const closesAtLocal = new Date(
    closesAtTimeZOne.toLocaleString("en-US", { timeZone })
  );

  if (now < opensAtLocal || now >= closesAtLocal) {
    return false;
  }

  return true;
}

if (document.querySelector("[data-location]")) {
  const script = document.createElement("script");
  script.src = "https://apis.google.com/js/api.js";
  script.async = true;
  script.defer = true;
  script.onload = () => {
    gapi.load("client", getSheetData)
  };
  document.head.appendChild(script);

  function getSheetData() {
    globalThis.fetchLocations().then((locations) => {
      document.querySelectorAll("[data-location]").forEach(pageLocation => {
        let bank = pageLocation.getAttribute("data-location");
        let key = pageLocation.getAttribute("data-key");
        if (!locations[bank] || !locations[bank][key]) {
          return;
        }
        let value = locations[bank][key]?.replaceAll("\n", "<br />");
        let element = pageLocation.getAttribute("data-element")
          ? pageLocation.querySelector(pageLocation.getAttribute("data-element"))
          : pageLocation

        if (value) {
          element.innerHTML = value;
          pageLocation.classList.remove("hidden");
        } else {
          pageLocation.classList.add("hidden");
        }

        if (key === "status") {
          element.classList.remove("bg-tertiary-2");
          switch (value) {
            case "Open":
              element.classList.add("bg-tertiary-2");
              break;
            case "Closed":
              element.classList.add("bg-tertiary-4");
              element.classList.add("text-white"); // text-white");
              break;
            case "Limited Hours":
            case "Lobby: Appt Only":
            case "Drive-Thru Only":
            default:
              element.classList.add("bg-secondary-2");
              break;
          }
        }

        if (key === "phone" && element.tagName === "A") {
          element.setAttribute("href", `tel:${value}`);
        }
      });
    }, reason => {
      if (typeof reason === "object" && reason.result) {
        console.error && console.error("Error: " + reason.result.error.message);
      } else {
        console.error && console.error(reason);
      }
    });
  }
}
